// Phase 4 (grade-2 ceiling) — `coins` concept visual for money.
//
// Renders a row/group of COINS (SVG circles) representing a money amount: a
// greedy quarter/dime/nickel/penny breakdown of a cents value — the standard
// "count the coins" manipulative. ADDITIVE — registers itself under the
// "coins" kind (see rendererRegistry); it plugs in with NO edit to MathVisual
// or the session screens (the registry resolves it by key), exactly like
// array / place_value_blocks / fraction_bar / clock. No skill targets it yet,
// so it stays invisible to kids until content opts in.
//
// FIELD-CONVENTION CONTRACT (read off the shared MathRendererProps — the
// future mapper branch in contentToEngineItem.ts MUST emit it this way):
//   • total <- the money amount IN CENTS (e.g. 67 -> 67 cents = 2 quarters,
//             1 dime, 1 nickel, 2 pennies). This is the ONLY field the
//             renderer STRICTLY needs; `a`/`b`/`op` are unused. Using cents
//             (an integer) keeps the contract identical in shape to the
//             other total-driven visuals (place_value_blocks) and avoids
//             float dollars. The greedy denomination split is deterministic.
//   • coinCounts <- MA-C16 (ADR-022 fast-follow), OPTIONAL — a literal named
//             coin multiset ({quarter,dime,nickel,penny} counts). When
//             present AND its cents value equals `total`, the renderer draws
//             EXACTLY this multiset instead of greedy-splitting `total` (so
//             a non-greedy-canonical prompt like "3 nickels" draws 3 nickel
//             circles, not a greedy 1-dime-1-nickel split). Absent, or
//             inconsistent with `total` (an authoring drift), => the legacy
//             greedy split, byte-identical to before.
// FAIL-SAFE: a non-finite/negative total clamps to 0 (renders no coins, an
// empty tray — never NaN). An oversized total is capped so the greedy split
// can't emit a runaway number of penny circles / a degenerate viewBox. The
// accessible label never states the CENTS SUM (that is the answer the child
// must compute by reading the coins) — it names only the drawn coins.
import type { MathRendererProps } from "./rendererRegistry";

// US coin denominations, largest first (greedy breakdown). `fill` is the coin
// face color; `r` its drawn radius (quarters largest, dimes smallest — true to
// life enough to read at a glance).
const COINS = [
  { name: "quarter", cents: 25, r: 22, fill: "var(--color-text-secondary, #7a6f60)" },
  { name: "nickel", cents: 5, r: 20, fill: "var(--pal-green, #5a9e6f)" },
  { name: "penny", cents: 1, r: 18, fill: "var(--pal-orange, #d97742)" },
  { name: "dime", cents: 10, r: 15, fill: "var(--color-border-secondary, #cdbfae)" },
] as const;

// Greedy order must be value-descending (25,10,5,1) regardless of draw size.
const ORDER = [...COINS].sort((x, y) => y.cents - x.cents);

const STROKE = "var(--color-text-secondary, #7a6f60)";
const GAP = 6; // px between coins
const PAD = 4; // outer padding
const MAX_CENTS = 999; // cap so the greedy split stays bounded (<= ~40 coins)

function CoinsRenderer({ total, coinCounts }: MathRendererProps) {
  // Clamp fail-safe: non-finite / negative -> 0; oversized -> MAX_CENTS so a
  // runaway value can't emit hundreds of penny circles.
  const raw = Number.isFinite(total) ? Math.max(0, Math.floor(total)) : 0;
  const cents = Math.min(raw, MAX_CENTS);

  // MA-C16: use the literal named multiset ONLY when every count is a
  // non-negative integer AND its cents value reconstructs `cents` exactly —
  // otherwise (absent, malformed, or drifted from the total) fall back to
  // the legacy greedy split, so a bad/partial payload can never draw a
  // multiset that contradicts the answer.
  const literalSum = coinCounts
    ? ORDER.reduce((s, coin) => {
        const raw = coinCounts[coin.name as keyof typeof coinCounts];
        const n = raw === undefined ? 0 : raw;
        return s + (Number.isInteger(n) && n >= 0 ? n * coin.cents : NaN);
      }, 0)
    : NaN;
  const useLiteral = coinCounts != null && literalSum === cents;

  // Denomination breakdown into a flat list of coin specs to draw: the
  // literal named multiset when valid, else the greedy split of `cents`.
  const drawn: { r: number; fill: string; name: string }[] = [];
  if (useLiteral) {
    for (const coin of ORDER) {
      const count = coinCounts![coin.name as keyof typeof coinCounts] ?? 0;
      for (let i = 0; i < count; i++) drawn.push({ r: coin.r, fill: coin.fill, name: coin.name });
    }
  } else {
    let remaining = cents;
    for (const coin of ORDER) {
      const count = Math.floor(remaining / coin.cents);
      remaining -= count * coin.cents;
      for (let i = 0; i < count; i++) drawn.push({ r: coin.r, fill: coin.fill, name: coin.name });
    }
  }

  // Lay coins out left-to-right on a single baseline, centered vertically on
  // the tallest coin so differing radii share a midline.
  const maxR = drawn.reduce((m: number, c) => Math.max(m, c.r), 0) || COINS[0].r;
  let x = PAD;
  const placed = drawn.map((c) => {
    const cx = x + c.r;
    x += c.r * 2 + GAP;
    return { cx, cy: PAD + maxR, ...c };
  });
  const width = Math.max(maxR * 2 + PAD * 2, x - GAP + PAD);
  const height = maxR * 2 + PAD * 2;

  // Build a readable label naming the drawn COINS only (e.g. "Coins: 2
  // quarters, 1 dime, 1 nickel, 2 pennies") — MA-C16: the cents SUM is the
  // answer the child must compute by reading the coins, so it is never
  // stated here (unlike the old label, which gave the sum away for free).
  const counts = ORDER.map((coin) => {
    const n = drawn.filter((d) => d.name === coin.name).length;
    // "penny" -> "pennies"; the rest take a plain "s".
    const plural = coin.name === "penny" ? "pennies" : `${coin.name}s`;
    return n ? `${n} ${n > 1 ? plural : coin.name}` : null;
  }).filter(Boolean);
  const label = counts.length ? `Coins: ${counts.join(", ")}` : "No coins";

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      style={{ maxWidth: Math.min(360, width) }}
      role="img"
      aria-label={label}
    >
      {placed.map((c, i) => (
        <circle key={i} cx={c.cx} cy={c.cy} r={c.r} fill={c.fill} stroke={STROKE} strokeWidth="2" />
      ))}
    </svg>
  );
}

export default CoinsRenderer;
